import React from 'react';
import { X, Minus, Plus, Trash2, ShoppingCart } from 'lucide-react';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

interface MiniCartProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (id: number, quantity: number) => void;
  onRemoveItem: (id: number) => void;
  onCheckout?: () => void;
}

const MiniCart: React.FC<MiniCartProps> = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout
}) => {
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleQuantityChange = (id: number, type: 'increase' | 'decrease') => {
    const item = cartItems.find(item => item.id === id);
    if (item) {
      if (type === 'increase') {
        onUpdateQuantity(id, item.quantity + 1);
      } else if (type === 'decrease' && item.quantity > 1) {
        onUpdateQuantity(id, item.quantity - 1);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white rounded-t-xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <ShoppingCart size={20} className="text-primary" />
            <h3 className="text-lg font-semibold">购物车</h3>
            <span className="text-sm text-gray-500">({totalItems}件)</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* 购物车内容 */}
        <div className="flex-1 overflow-y-auto max-h-[60vh]">
          {cartItems.length === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">购物车是空的</p>
              <p className="text-sm text-gray-400">快去添加一些商品吧</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-3 p-3 border border-gray-100 rounded-lg">
                  {/* 商品图片 */}
                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {item.image.startsWith('/') ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    {/* 备用显示 */}
                    <div className={`w-full h-full flex items-center justify-center text-2xl ${item.image.startsWith('/') ? 'hidden' : ''}`}>
                      🍽️
                    </div>
                  </div>

                  {/* 商品信息 */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-800 text-sm line-clamp-2 mb-1">
                      {item.name}
                    </h4>
                    <p className="text-red-600 font-semibold">¥{item.price}</p>
                  </div>

                  {/* 数量控制 */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleQuantityChange(item.id, 'decrease')}
                      disabled={item.quantity <= 1}
                      className="p-1 rounded-full border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-8 text-center text-sm">{item.quantity}</span>
                    <button
                      onClick={() => handleQuantityChange(item.id, 'increase')}
                      className="p-1 rounded-full border border-gray-300 hover:bg-gray-50"
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  {/* 删除按钮 */}
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部结算 */}
        {cartItems.length > 0 && (
          <div className="border-t p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-semibold">总计:</span>
              <span className="text-xl font-bold text-red-600">¥{totalPrice.toFixed(2)}</span>
            </div>
            <button 
              onClick={onCheckout || (() => {})}
              className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              去结算 ({totalItems}件)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniCart;
